import React, { useMemo, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  Calculator,
  CalendarDays,
  CalendarClock,
  CalendarCheck,
  ChevronDown,
  Clock,
  FileText,
  FileStack,
  Fingerprint,
  HandCoins,
  LayoutGrid,
  Landmark,
  Menu,
  X,
  Receipt,
  TrendingUp,
  Users,
  Wallet,
  ClipboardList,
} from "lucide-react";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import "./css/sidebar.css";

type SidebarNavLeafItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
};

type SidebarNavDividerItem = {
  key: string;
  type: "divider";
};

type SidebarNavParentItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  children: SidebarNavLeafItem[];
};

type SidebarNavItem = SidebarNavLeafItem | SidebarNavParentItem | SidebarNavDividerItem;

type SidebarProps = {
  variant?: "default" | "financial" | "tax-reconciliation";
};

export default function Sidebar({ variant = "default" }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleCollapsed, expandedSections, toggleSection, activePath, setActivePath, setSectionExpanded } = useSidebar();

  const { user } = useAuth();
  const roleid = user?.roleid ?? null;
  const usesBiometrics = user?.company?.biometrics === true;
  const currentEmployeeId =
    new URLSearchParams(location.search).get("employeeId") ?? sessionStorage.getItem("payrollEmployeeId");
  const withEmployeeQuery = (to: string) => {
    if (!currentEmployeeId || !to.startsWith("/payroll/")) {
      return to;
    }

    const separator = to.includes("?") ? "&" : "?";
    return `${to}${separator}employeeId=${currentEmployeeId}`;
  };

  const items = useMemo(
    (): SidebarNavItem[] => {
      if (variant === "financial") {
        return [
          { key: "pay-register", label: "Pay Register", to: "/report/financial?tab=pay-register", icon: <Calculator size={18} /> },
          { key: "payroll-totals", label: "Payroll Totals", to: "/report/financial?tab=payroll-totals", icon: <HandCoins size={18} /> },
          { key: "loans", label: "Loans", to: "/report/financial?tab=loans", icon: <Landmark size={18} /> },
          { key: "net-pay", label: "Net Pay List", to: "/report/financial?tab=net-pay", icon: <CalendarDays size={18} /> },
          {
            key: "tax-incentive",
            label: "Consolidated Tax Incentive",
            to: "/report/financial?tab=tax-incentive",
            icon: <Building2 size={18} />,
          },
        ];
      }

      if (variant === "tax-reconciliation") {
        return [
          {
            key: "employee-tax-report",
            label: "Employee Tax Report",
            to: "/report/tax-reconciliation?tab=employee-tax-report",
            icon: <FileStack size={18} />,
          },
          {
            key: "paye-totals",
            label: "PAYE Totals",
            to: "/report/tax-reconciliation?tab=paye-totals",
            icon: <Calculator size={18} />,
          },
        ];
      }

      if (roleid === 3 || roleid === "3") {
        return [
          { key: "biometrics", label: "Dashboard", to: "/biometrics", icon: <LayoutGrid size={18} /> },
          { key: "attendance-calendar", label: "Attendance Calendar", to: "/attendance-calendar", icon: <CalendarDays size={18} /> },
          { key: "history", label: "History", to: "/history", icon: <FileText size={18} /> },
        ];
      }

      if (roleid === 7 || roleid === "7") {
        return [
          { key: "kitchen-usage", label: "Monthly Usage", to: "/kitchen/daily-usage", icon: <CalendarCheck size={18} /> },
          { key: "kitchen-reservations", label: "Reservations", to: "/kitchen/reservations", icon: <CalendarCheck size={18} /> },
          { key: "kitchen-intake", label: "Monthly Intake", to: "/kitchen/intake", icon: <CalendarDays size={18} /> },
          { key: "kitchen-menu", label: "Menu Items", to: "/kitchen/menu", icon: <ClipboardList size={18} /> },
          { key: "kitchen-analytics", label: "Analytics", to: "/kitchen/analytics", icon: <BarChart3 size={18} /> },
        ];
      }

      const baseItems: SidebarNavItem[] = [
        { key: "dashboard", label: "Dashboard", to: "/dashboard", icon: <LayoutGrid size={18} /> },
        {
          key: "company-management",
          label: "Company Management",
          to: "/company-management",
          icon: <Building2 size={18} />,
        },
        { key: "pay-cycle", label: "Pay Cycle", to: "/pay-cycle", icon: <CalendarClock size={18} /> },
        { key: "employee-management", label: "Employee Management", to: "/employee-management", icon: <Users size={18} /> },
        {
          key: "payroll",
          label: "Payroll",
          icon: <Wallet size={18} />,
          children: [
            { key: "hours-rates", label: "Hours and Rates", to: "/payroll/hours-and-rates", icon: <Clock size={16} /> },
            { key: "payslip", label: "Payslip", to: "/payroll/payslip", icon: <FileText size={16} /> },
            { key: "transactions", label: "Transactions", to: "/payroll/transactions", icon: <Receipt size={16} /> },
            {
              key: "annual-values",
              label: "Annual Values",
              to: "/payroll/annual-values",
              icon: <TrendingUp size={16} />,
            },
            {
              key: "leave-applications",
              label: "Leave",
              to: "/payroll/leave-applications",
              icon: <FileText size={16} />,
            },
          ],
        },
        {
          key: "biometrics",
          label: "Biometrics",
          icon: <Fingerprint size={18} />,
          children: [
            { key: "attendance-calendar", label: "Attendance Calendar", to: "/attendance-calendar", icon: <CalendarDays size={16} /> },
            { key: "history", label: "History", to: "/history", icon: <FileText size={16} /> },
          ],
        },
        { key: "report", label: "Reports", to: "/report", icon: <BarChart3 size={18} /> },
      ];

      // Only companies that use biometrics get the Biometrics section.
      return usesBiometrics ? baseItems : baseItems.filter((item) => item.key !== "biometrics");
    },
    [roleid, variant, usesBiometrics]
  );

  useEffect(() => {
    const biometricPaths = ["/biometrics", "/history", "/attendance-calendar"];
    if (!biometricPaths.includes(location.pathname)) {
      setSectionExpanded("biometrics", false);
    }
  }, [location.pathname, setSectionExpanded]);

  const isActive = (to: string) => {
    if (variant === "financial" || variant === "tax-reconciliation") {
      return `${location.pathname}${location.search}` === to;
    }
    if (to === "/dashboard") return activePath === "/dashboard";
    return activePath === to || activePath.startsWith(`${to}/`);
  };

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header">
        <button type="button" className="toggle-btn" onClick={toggleCollapsed} aria-label="Toggle sidebar">
          {isCollapsed ? <Menu size={18} /> : <X size={18} />}
        </button>
      </div>
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {items.map((item) => {
            if ("type" in item && item.type === "divider") {
              return <li key={item.key} className="sidebar-divider" />;
            }

            const leafItem = item as SidebarNavLeafItem;

            if ("children" in item) {
              const open = !!expandedSections[item.key];
              const anyChildActive = item.children.some((c) => isActive(c.to)) || isActive(`/${item.key}`);

              return (
                <li key={item.key} className={`nav-item ${anyChildActive ? "active" : ""}`}>
                  <button
                    type="button"
                    className="nav-link nav-link--button"
                    onClick={() => {
                      if (item.key === "payroll") {
                        setSectionExpanded("payroll", false);
                        setActivePath("/payroll");
                        navigate("/payroll");
                        return;
                      }
                      if (item.key === "biometrics") {
                        setSectionExpanded("biometrics", true);
                        setActivePath("/biometrics");
                        navigate("/biometrics");
                        return;
                      }
                      toggleSection(item.key);
                    }}
                    aria-expanded={open}
                  >
                    <div className="nav-link-left">
                      {item.icon}
                      {!isCollapsed && <span>{item.label}</span>}
                    </div>
                    {!isCollapsed && ((item.key !== "payroll" && item.key !== "biometrics") || open) && (
                      <ChevronDown size={16} className={`nav-chevron ${open ? "open" : ""}`} />
                    )}
                  </button>

                  {!isCollapsed && open && (
                    <ul className="subnav-list">
                      {item.children.map((child) => (
                        <li key={child.key} className={`subnav-item ${isActive(child.to) ? "active" : ""}`}>
                          <NavLink
                            to={withEmployeeQuery(child.to)}
                            className="subnav-link"
                            onClick={() => setActivePath(child.to)}
                          >
                            {child.icon}
                            <span>{child.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            return (
              <li key={leafItem.key} className={`nav-item ${isActive(leafItem.to) ? "active" : ""}`}>
                <NavLink to={leafItem.to} className="nav-link" onClick={() => setActivePath(leafItem.to)}>
                  {leafItem.icon}
                  {!isCollapsed && (
                    <div className="nav-link-label">
                      <span>{leafItem.label}</span>
                    </div>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
